import { EditableGrid, EditorField } from "@/components/common/EditableGrid";
import { TabContent } from "@/components/common/PageLayout";
import { ShowAccordionSection } from "@/components/common/ShowAccordionSection";
import { useRecordContext } from "react-admin";
import { ResourceName, Policy, type Reimbursement } from "@/types";
import { Grid2 as Grid } from "@mui/material";
import { formatYearRange } from "@/utils/formatters/dateFormatter";
import { formatToTitleCase } from "@/utils/formatters/stringFormatters";
import { FinancialBreakdown } from "./FinancialBreakdown";
import { LinkedClaimCard } from "./LinkedClaimCard";
import { loadKeyLockReason } from "../utils/loadKeyLock";

export const ReimbursementOverviewTab = () => {
  const record = useRecordContext<Reimbursement>();
  return (
    <TabContent>
      <Grid container spacing={2}>
        {/* Left — Reimbursement Details + Linked Claim */}
        <Grid size={{ xs: 12, lg: 7 }}>
          <ShowAccordionSection label="Reimbursement Details" defaultExpanded>
            <EditableGrid fields={getClaimFields(record)} recordType={ResourceName.REIMBURSEMENTS} columnsPerRow={3} />
          </ShowAccordionSection>

          <ShowAccordionSection label="Linked Claim" defaultExpanded>
            <LinkedClaimCard />
          </ShowAccordionSection>
        </Grid>

        {/* Right — Financial Breakdown */}
        <Grid size={{ xs: 12, lg: 5 }}>
          <ShowAccordionSection label="Financial Breakdown" defaultExpanded>
            <FinancialBreakdown />
          </ShowAccordionSection>
        </Grid>
      </Grid>
    </TabContent>
  );
};

const getClaimFields = (record?: Reimbursement): EditorField[] => {
  return [
    {
      source: "claimant_id_ref",
      type: "reference",
      reference: ResourceName.CLAIMANTS,
      displayField: "claimant_name",
      readOnlyReason: "Use Reassign to change relationships",
      section: "Overview",
    },
    {
      source: "client_id_ref",
      type: "reference",
      reference: ResourceName.CLIENTS,
      displayField: "client_legal_name",
      readOnlyReason: "Use Reassign to change relationships",
      section: "Overview",
    },
    {
      source: "policy_id_ref",
      type: "reference",
      reference: ResourceName.POLICIES,
      displayField: "vendor_company_name",
      getOptionLabel: (policy: Policy) => {
        const carrier =
          (policy as Record<string, string>).vendor_company_name ||
          policy.source_data?.stop_loss_carrier ||
          "No Carrier";
        const years = formatYearRange(policy.effective_start_date, policy.effective_end_date);
        return [carrier, years, formatToTitleCase(policy.status)].filter(Boolean).join(" · ");
      },
      readOnlyReason: "Use Reassign to change relationships",
      section: "Overview",
    },
    // Re-upload match-key fields are read-only on a file-loaded row (#7560).
    {
      source: "received_date",
      type: "date",
      section: "Details",
      readOnlyReason: loadKeyLockReason(record, "received_date"),
    },
    { source: "claims_administrator", section: "Details" },
    { source: "provider", section: "Details", readOnlyReason: loadKeyLockReason(record, "provider") },
    { source: "payee", section: "Details" },
    { source: "auditor_name", section: "Details" },
    {
      source: "service_start_date",
      type: "date",
      section: "Dates",
      readOnlyReason: loadKeyLockReason(record, "service_start_date"),
    },
    { source: "service_end_date", type: "date", section: "Dates" },
  ];
};
